import { useState, useEffect } from "react";
import { Track } from "@/types";
import { supabase } from "@/integrations/supabase/client";
import { useMusic } from "@/contexts/MusicContext";

interface UseStreamHandlingProps {
  currentTrack: Track | null;
}

export function useStreamHandling({ currentTrack }: UseStreamHandlingProps) {
  const [liked, setLiked] = useState(false);
  const { playTrack, currentQueue, currentTrackIndex, playNext, setIsPlaying } =
    useMusic();

  // Reset liked state when track changes
  useEffect(() => {
    if (currentTrack) {
      setLiked(false);
    }
  }, [currentTrack]);

  // Toggle like status
  const toggleLike = () => {
    setLiked(!liked);
  };

  // Get random verified artist track
  const getRandomVerifiedTrack = async (): Promise<Track | null> => {
    try {
      console.log("🎵 Searching for random verified artist track...");

      // Use targeted RPC for verified artists
      const { data: verifiedArtists, error: profilesError } =
        await supabase.rpc("get_verified_artists_display", { p_limit: 50 });

      if (profilesError || !verifiedArtists) {
        console.log("❌ Error fetching verified artists:", profilesError);
        return null;
      }

      if (verifiedArtists.length === 0) {
        console.log("❌ No verified artists with valid verification found");
        return null;
      }

      const artistIds = verifiedArtists.map((a: any) => a.id);
      console.log(
        `🎭 Found ${artistIds.length} verified artists with valid verification`,
      );

      // Get tracks from verified artists (excluding current track)
      const { data: tracks, error: tracksError } = await supabase
        .from("tracks")
        .select("*")
        .eq("approval_status", "approved")
        .in("artist_id", artistIds)
        .neq("id", currentTrack?.id || "");

      if (tracksError || !tracks?.length) {
        console.log("❌ No tracks found from verified artists:", tracksError);
        return null;
      }

      // Randomly select a track
      const randomTrack = tracks[Math.floor(Math.random() * tracks.length)];
      const artist = verifiedArtists.find(
        (a: any) => a.id === randomTrack.artist_id,
      );
      console.log(
        `🎲 Selected random track: "${randomTrack.title}" by ${artist?.display_name}`,
      );

      return {
        id: randomTrack.id,
        title: randomTrack.title,
        artist: artist?.display_name || "Unknown Artist",
        artistId: randomTrack.artist_id,
        audioUrl: randomTrack.audio_url,
        coverUrl: randomTrack.cover_url,
        duration: randomTrack.duration,
        isSponsored: randomTrack.is_sponsored,
        sponsorId: randomTrack.sponsor_id,
        streams: randomTrack.streams,
        createdAt: randomTrack.created_at,
        genre: randomTrack.genre,
        description: randomTrack.description,
        isExplicit: randomTrack.is_explicit,
        likes_count: randomTrack.likes_count,
        albumTitle: randomTrack.album_title,
        releaseDate: randomTrack.release_date,
      };
    } catch (error) {
      console.error("❌ Error getting random verified track:", error);
      return null;
    }
  };

  // Handle stream completion
  const handleStreamComplete = async () => {
    console.log("🎯 Stream completed for:", currentTrack?.title);

    if (currentTrack?.isSponsored) {
      // This will be handled by the TrackStreamDetector component
      console.log("🎪 Sponsored track completed");
    }

    // First check if there's a next track in the current queue
    if (
      currentQueue.length > 0 &&
      currentTrackIndex < currentQueue.length - 1
    ) {
      console.log("🎵 Playing next track in queue");
      playNext(); // No delay needed - transition state handles timing
      return;
    }

    // If no more tracks in queue, try to get a random verified track
    try {
      const nextTrack = await getRandomVerifiedTrack();
      if (nextTrack) {
        console.log("🎵 Auto-playing next random track:", nextTrack.title);
        playTrack(nextTrack); // No delay needed - transition state handles timing
      } else {
        console.log("🔇 No verified tracks available for auto-play");
        setIsPlaying(false);
      }
    } catch (error) {
      console.error("❌ Error in auto-play:", error);
      setIsPlaying(false);
    }
  };

  return {
    liked,
    toggleLike,
    handleStreamComplete,
  };
}
